import { lazy, Suspense, useEffect, useId, useState } from "react";
import type { LessonNoteGroove } from "./api.js";
import type { GroovePracticeInstruction } from "./GrooveSnapshotPlayer.js";
import type { GrooveLearnTempoSave } from "./GrooveLearnMode.js";

// Keep the engraver/audio code out of lesson-history bundles until somebody opens a player.
const GrooveSnapshotPlayer = lazy(() => import("./GrooveSnapshotPlayer.js").then((module) => ({
  default: module.GrooveSnapshotPlayer,
})));
const GrooveLearnMode = lazy(() => import("./GrooveLearnMode.js").then((module) => ({
  default: module.GrooveLearnMode,
})));

interface SnapshotPayload {
  title: string;
  query: string;
  practiceInstructions?: GroovePracticeInstruction[];
}

export function LessonNoteGrooves({ grooves, activeToken, onSelect }: {
  grooves: readonly LessonNoteGroove[];
  activeToken: string | null;
  onSelect: (token: string | null) => void;
}) {
  if (grooves.length === 0) return null;
  return (
    <section className="family-lesson-grooves" aria-label="Grooves from this lesson">
      <h3>Grooves from this lesson</h3>
      {grooves.map((groove) => (
        <div className="family-lesson-groove" key={groove.id}>
          <strong>{groove.title}</strong>
          <LessonGroovePlayer groove={groove} activeToken={activeToken} onSelect={onSelect} />
        </div>
      ))}
    </section>
  );
}

/** One active player per notes list, so choosing another disposes the old audio. */
export function LessonGroovePlayer({ groove, query, tempoSave, activeToken, onSelect, compact = false }: {
  groove: LessonNoteGroove;
  /** Tutor write-ups already hold the saved lesson snapshot. */
  query?: string;
  tempoSave?: GrooveLearnTempoSave;
  activeToken: string | null;
  onSelect: (token: string | null) => void;
  compact?: boolean;
}) {
  const playerId = useId();
  const open = activeToken === groove.token;
  return (
    <div className="lesson-inline-player">
      <div className="lesson-inline-player-actions">
        <button
          type="button"
          className="quiet"
          aria-expanded={open}
          aria-controls={playerId}
          aria-label={`${open ? "Close player for" : "Play and change tempo for"} ${groove.title}`}
          onClick={() => onSelect(open ? null : groove.token)}
        >
          {open ? "Close player" : "Play / change tempo"}
        </button>
        {!open && query !== undefined && <Suspense fallback={<button type="button" className="quiet" disabled>Learn mode</button>}>
          <GrooveLearnMode query={query} title={groove.title} buttonAriaLabel={`Learn mode for ${groove.title}`} {...tempoSave} />
        </Suspense>}
      </div>
      <div id={playerId}>
        {open && <LessonSnapshotPlayback token={groove.token} query={query} tempoSave={tempoSave} compact={compact} />}
      </div>
    </div>
  );
}

function LessonSnapshotPlayback({ token, query, tempoSave, compact }: { token: string; query?: string; tempoSave?: GrooveLearnTempoSave; compact: boolean }) {
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "missing" }
    | { status: "ready"; snapshot: SnapshotPayload }
  >({ status: "loading" });

  useEffect(() => {
    let live = true;
    setState({ status: "loading" });
    void (async () => {
      try {
        const response = await fetch(`/api/public/grooves/${encodeURIComponent(token)}`);
        if (!response.ok) {
          if (live) setState({ status: "missing" });
          return;
        }
        const snapshot = (await response.json()) as SnapshotPayload;
        if (live) setState({ status: "ready", snapshot });
      } catch {
        if (live) setState({ status: "missing" });
      }
    })();
    return () => { live = false; };
  }, [token]);

  if (state.status === "loading") return <p role="status">Loading groove player…</p>;
  if (state.status === "missing") return <p className="small muted">That groove is not here. The lesson note remains available.</p>;

  return (
    <Suspense fallback={<p role="status">Loading groove player…</p>}>
      <GrooveSnapshotPlayer
        title={state.snapshot.title}
        query={query ?? state.snapshot.query}
        tempoSave={tempoSave}
        practiceInstructions={state.snapshot.practiceInstructions ?? []}
        embedded
        compact={compact}
      />
    </Suspense>
  );
}
